// Runtime configuration for the shared contract layer.
//
// Everything the contract layer needs from its host platform is passed in
// here, so `shared/` itself reads no build-time globals (`import.meta.env`),
// no `window`, and no module-level singletons. The web app builds this from
// `VITE_API_URL` + a `localStorage` `TokenStore`; RN builds it from its own
// env + a Keychain `TokenStore`.

import type { TokenStore } from "./tokenStore";

/**
 * Everything `createClient` needs from the host platform.
 *
 * `apiBase` is the backend origin (no trailing slash), resolved by the host
 * (web: `VITE_API_URL`). `store` is the injected session seam. The optional
 * `fetch` lets a host pass a non-global fetch (RN test harnesses, polyfills);
 * it defaults to `globalThis.fetch`, which exists in both modern web and RN.
 *
 * `onSessionSaved` / `onSessionCleared` are optional side-channel hooks fired
 * AFTER the store is mutated — the web app uses them to drive PostHog
 * identify/reset, which is a presentation concern that must not leak into the
 * neutral layer. They are deliberately fire-and-forget (errors are the host's
 * problem) and never block the auth path.
 */
export interface ClientConfig {
  /** Backend origin, e.g. `https://api.waytide.app` (no trailing slash). */
  apiBase: string;
  /** Injected session persistence (localStorage on web, Keychain on RN). */
  store: TokenStore;
  /** Optional fetch override; defaults to the platform global `fetch`. */
  fetch?: typeof fetch;
  /** Fired after a session is persisted (e.g. analytics identify). */
  onSessionSaved?: (resp: import("./tokenStore").AuthResponse) => void;
  /** Fired after a session is cleared (e.g. analytics reset). */
  onSessionCleared?: () => void;
}

/**
 * Perform a fetch for a config, resolving the implementation LAZILY at call
 * time: the explicit `config.fetch` override if given, else the live
 * `globalThis.fetch`. Resolving per-call (not once at client construction) is
 * deliberate — it matches the original direct `fetch(...)` call sites, so a
 * host (or a test) that swaps the global `fetch` after building the client is
 * still honoured. Always invoked with `globalThis` as the receiver, never a
 * captured binding. Every transport (`request`, the SSE reader) goes through
 * here so they resolve fetch identically.
 */
export function callFetch(
  config: ClientConfig,
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const f = config.fetch ?? globalThis.fetch;
  // The `as RequestInfo` narrows for non-DOM lib targets (React Native's TS
  // types exclude `URL` from `RequestInfo`; the DOM's accept it). Runtime is
  // identical — every transport here passes a string URL anyway. Upstreamed
  // from the M0 mobile port so vendored copies compile under RN TS verbatim.
  return f.call(globalThis, input as RequestInfo, init);
}
